import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { DropdownMenuItem } from "./ui/dropdown-menu";
import { Bell, Loader2, Send, RefreshCw, History } from "lucide-react";
import { isNativeFamilyApp } from "./PWAInstallPrompt";
import { isNativeOssApp } from "../lib/oss/bridge";

function isNativePushApp() {
  return isNativeFamilyApp() || isNativeOssApp();
}

function nativePushBridge() {
  if (typeof window === "undefined") return null;
  if (isNativeOssApp()) return window.OssApp || null;
  if (isNativeFamilyApp()) return window.FamilyApp || null;
  return null;
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/** Push attive solo con build produzione (SW registrato in index.js) e contesto sicuro. */
export function isPushEnvironment() {
  return (
    typeof window !== "undefined" &&
    process.env.NODE_ENV === "production" &&
    window.isSecureContext &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

function nativePushReady() {
  const bridge = nativePushBridge();
  return Boolean(
    bridge &&
      typeof bridge.isNativePushSupported === "function" &&
      bridge.isNativePushSupported()
  );
}

async function ensureServiceWorkerRegistration() {
  if (!("serviceWorker" in navigator)) {
    throw new Error("Service worker non supportato");
  }
  let registration = await navigator.serviceWorker.getRegistration("/");
  if (!registration) {
    registration = await navigator.serviceWorker.register("/sw.js");
  }
  await navigator.serviceWorker.ready;
  return registration;
}

export function usePushNotifications(token, options = {}) {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const {
    statusPath = "/api/push/status",
    subscribePath = "/api/push/subscribe",
    /** Se true, "subscribed" richiede anche una subscription PushManager locale (tablet OSS). */
    requireLocalSubscription = false,
  } = options;
  const [supported, setSupported] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  const nativePush = isNativePushApp();

  const refreshStatus = useCallback(async () => {
    if (!token) return;
    try {
      const [cfgRes, statusRes] = await Promise.all([
        fetch(`${API_URL}/api/push/vapid-public-key`),
        fetch(`${API_URL}${statusPath}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      const cfg = await cfgRes.json();
      const status = statusRes.ok ? await statusRes.json() : {};
      setConfigured(
        nativePush
          ? Boolean((cfg.fcm_configured || status.fcm_configured) && nativePushReady())
          : Boolean(cfg.configured && cfg.public_key)
      );
      let serverSubscribed = Boolean(status.subscribed);
      if (requireLocalSubscription && serverSubscribed && isPushEnvironment() && !nativePush) {
        try {
          const registration = await ensureServiceWorkerRegistration();
          const local = await registration.pushManager.getSubscription();
          serverSubscribed = Boolean(local);
        } catch {
          serverSubscribed = false;
        }
      }
      setSubscribed(serverSubscribed);
    } catch (error) {
      console.error("Push status error:", error);
    }
  }, [API_URL, token, statusPath, requireLocalSubscription, nativePush]);

  useEffect(() => {
    const ok = nativePush || isPushEnvironment();
    setSupported(ok);
    if (token && ok) {
      refreshStatus();
    }
  }, [token, refreshStatus, nativePush]);

  const subscribeNative = useCallback(() => {
    const bridge = nativePushBridge();
    if (!bridge || typeof bridge.enablePush !== "function") {
      toast.error("Notifiche native non disponibili in questa app");
      return Promise.resolve(false);
    }
    return new Promise((resolve) => {
      window.__affidyaOnFcmToken = async (fcmToken) => {
        window.__affidyaOnFcmToken = null;
        if (!fcmToken) {
          toast.error("Permesso notifiche negato oppure Firebase non è configurato sull'app");
          resolve(false);
          return;
        }
        try {
          const res = await fetch(`${API_URL}${subscribePath}`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              channel: "fcm",
              fcm_token: fcmToken,
              endpoint: `fcm:${fcmToken}`,
              keys: { p256dh: "fcm", auth: "fcm" },
            }),
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.detail || "Errore registrazione push");
          }
          setSubscribed(true);
          toast.success("Notifiche push attivate");
          resolve(true);
        } catch (error) {
          console.error("Push subscribe error:", error);
          toast.error(error.message || "Errore attivazione push");
          resolve(false);
        }
      };
      bridge.enablePush();
    });
  }, [API_URL, subscribePath, token]);

  const subscribe = useCallback(async () => {
    if (!token || !supported) {
      toast.error("Notifiche push non supportate in questo browser");
      return false;
    }
    setLoading(true);
    try {
      if (nativePush) {
        return await subscribeNative();
      }
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Permesso notifiche negato");
        return false;
      }
      const cfgRes = await fetch(`${API_URL}/api/push/vapid-public-key`);
      const cfg = await cfgRes.json();
      if (!cfg.public_key) {
        toast.error("Push non configurate sul server");
        return false;
      }
      const registration = await ensureServiceWorkerRegistration();
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(cfg.public_key),
        });
      }
      const subJson = subscription.toJSON();
      const res = await fetch(`${API_URL}${subscribePath}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          endpoint: subJson.endpoint,
          keys: subJson.keys,
          expirationTime: subJson.expirationTime,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Errore registrazione push");
      }
      setSubscribed(true);
      toast.success("Notifiche push attivate");
      return true;
    } catch (error) {
      console.error("Push subscribe error:", error);
      toast.error(error.message || "Errore attivazione push");
      return false;
    } finally {
      setLoading(false);
    }
  }, [API_URL, nativePush, subscribeNative, supported, token, subscribePath]);

  const unsubscribe = useCallback(async () => {
    if (!token) return false;
    setLoading(true);
    try {
      if (nativePush) {
        const fcmToken = nativePushBridge()?.currentFcmToken?.() || "";
        if (fcmToken) {
          await fetch(`${API_URL}${subscribePath}`, {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ endpoint: `fcm:${fcmToken}` }),
          });
        }
        setSubscribed(false);
        toast.success("Notifiche push disattivate");
        return true;
      }
      const registration = await ensureServiceWorkerRegistration();
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await fetch(`${API_URL}${subscribePath}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ endpoint: subscription.endpoint }),
        });
        await subscription.unsubscribe();
      }
      setSubscribed(false);
      toast.success("Notifiche push disattivate");
      return true;
    } catch (error) {
      console.error("Push unsubscribe error:", error);
      toast.error("Errore disattivazione push");
      return false;
    } finally {
      setLoading(false);
    }
  }, [API_URL, nativePush, token, subscribePath]);

  return {
    supported,
    configured,
    subscribed,
    loading,
    subscribe,
    unsubscribe,
    refreshStatus,
  };
}

export default function PushNotificationsSettings({ token }) {
  const { supported, configured, subscribed, loading, subscribe, unsubscribe } =
    usePushNotifications(token);

  if (!supported) {
    return (
      <Card className="card-base">
        <CardHeader className="p-4 md:p-6">
          <CardTitle className="flex items-center gap-2 text-base md:text-lg">
            <Bell className="w-4 h-4 md:w-5 md:h-5 text-emerald-500" />
            Notifiche push
          </CardTitle>
          <CardDescription className="text-xs md:text-sm">
            Richiedono HTTPS, browser compatibile e app in produzione (PWA consigliata su iPhone/iPad)
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="card-base">
      <CardHeader className="p-4 md:p-6">
        <CardTitle className="flex items-center gap-2 text-base md:text-lg">
          <Bell className="w-4 h-4 md:w-5 md:h-5 text-emerald-500" />
          Notifiche push
        </CardTitle>
        <CardDescription className="text-xs md:text-sm">
          Ricevi avvisi sul dispositivo per terapie, documenti, fatture e altro
        </CardDescription>
      </CardHeader>
      <CardContent className="p-4 md:p-6 pt-0 space-y-4">
        {!configured && (
          <p className="text-sm text-amber-700 bg-amber-50 rounded-lg p-3">
            Le notifiche push non sono ancora configurate sul server (chiavi VAPID).
          </p>
        )}
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label className="text-sm font-medium">Stato</Label>
            <p className="text-xs text-slate-500 mt-0.5">
              {subscribed ? "Attive su questo dispositivo" : "Non attive"}
            </p>
          </div>
          <Badge className={subscribed ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}>
            {subscribed ? "Attive" : "Off"}
          </Badge>
        </div>
        <div className="flex gap-2">
          {!subscribed ? (
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={loading || !configured}
              onClick={subscribe}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Attiva notifiche
            </Button>
          ) : (
            <Button size="sm" variant="outline" disabled={loading} onClick={unsubscribe}>
              Disattiva
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

const PUSH_PROMPT_DISMISS_MS = 7 * 24 * 60 * 60 * 1000;

/** Banner post-login per attivare le notifiche push (richiede gesto utente per il permesso browser). */
export function PushNotificationPrompt({
  token,
  storageKey = "push-prompt-dismissed",
  title = "Attiva notifiche push",
  description = "Ricevi avvisi su terapie, documenti firmati, fatture SDI, PAI e altro direttamente sul dispositivo.",
  pushOptions,
  iconWrapClassName = "w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0",
  iconClassName = "w-5 h-5 text-emerald-700",
  actionClassName = "flex-1 h-9 text-xs bg-emerald-600 hover:bg-emerald-700",
}) {
  const { supported, configured, subscribed, loading, subscribe } =
    usePushNotifications(token, pushOptions);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    if (!token || !supported || !configured || subscribed) {
      setShowPrompt(false);
      return undefined;
    }
    const dismissed = localStorage.getItem(storageKey);
    if (dismissed) {
      const t = parseInt(dismissed, 10);
      if (!Number.isNaN(t) && Date.now() - t < PUSH_PROMPT_DISMISS_MS) {
        return undefined;
      }
    }
    if (typeof Notification !== "undefined" && Notification.permission === "denied" && !isNativePushApp()) {
      return undefined;
    }
    const timer = setTimeout(() => setShowPrompt(true), 1500);
    return () => clearTimeout(timer);
  }, [token, supported, configured, subscribed, storageKey]);

  const handleDismiss = () => {
    localStorage.setItem(storageKey, Date.now().toString());
    setShowPrompt(false);
  };

  const handleEnable = async () => {
    const ok = await subscribe();
    if (ok) {
      setShowPrompt(false);
    }
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-6 md:max-w-sm bg-white rounded-2xl shadow-2xl border border-slate-200 p-4 z-50 animate-in slide-in-from-bottom-5">
      <button
        type="button"
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-slate-400 hover:text-slate-600"
        aria-label="Chiudi"
      >
        ×
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className={iconWrapClassName}>
          <Bell className={iconClassName} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-slate-900 text-sm">{title}</h3>
          <p className="text-xs text-slate-500 mt-1 leading-relaxed">{description}</p>
        </div>
      </div>
      <div className="flex gap-2 mt-4">
        <Button variant="outline" size="sm" onClick={handleDismiss} className="flex-1 h-9 text-xs">
          Non ora
        </Button>
        <Button
          size="sm"
          onClick={handleEnable}
          disabled={loading}
          className={actionClassName}
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
          Attiva
        </Button>
      </div>
    </div>
  );
}

export const FAMILY_PUSH_OPTIONS = {
  statusPath: "/api/family/push/status",
  subscribePath: "/api/family/push/subscribe",
  requireLocalSubscription: true,
};

export function FamilyPushNotificationPrompt({ token }) {
  return (
    <PushNotificationPrompt
      token={token}
      storageKey="family-push-prompt-dismissed"
      title="Attiva le notifiche"
      description="Ricevi un avviso quando la struttura pubblica un Momento o aggiorna una tua richiesta."
      pushOptions={FAMILY_PUSH_OPTIONS}
      iconWrapClassName="w-11 h-11 rounded-xl bg-[#EDE9F6] flex items-center justify-center flex-shrink-0"
      iconClassName="w-5 h-5 text-[#6e48b5]"
      actionClassName="flex-1 h-9 text-xs bg-[#6e48b5] hover:bg-[#5a3b94]"
    />
  );
}

export function FamilyPushSettings({ token }) {
  const { supported, configured, subscribed, loading, subscribe, unsubscribe } =
    usePushNotifications(token, FAMILY_PUSH_OPTIONS);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-start gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#EDE9F6] text-[#6e48b5]">
          <Bell className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-extrabold text-[#304762]">Notifiche</p>
          <p className="mt-1 text-sm text-[#72839a]">
            Avviso sul telefono per i nuovi Momenti e quando la struttura aggiorna una tua richiesta.
          </p>
          {!supported ? (
            <p className="mt-3 text-xs text-slate-500">
              Le notifiche richiedono l&apos;app installata (PWA o app Family per Android e iOS) e una connessione sicura.
            </p>
          ) : !configured ? (
            <p className="mt-3 text-xs text-amber-700">Le notifiche non sono ancora configurate sul server.</p>
          ) : (
            <div className="mt-4 flex items-center justify-between gap-3">
              <p className="text-xs text-slate-500">{subscribed ? "Attive su questo dispositivo" : "Non attive su questo dispositivo"}</p>
              {subscribed ? (
                <Button type="button" variant="outline" disabled={loading} onClick={unsubscribe} className="h-9 rounded-xl text-xs">
                  Disattiva
                </Button>
              ) : (
                <Button type="button" disabled={loading} onClick={subscribe} className="h-9 rounded-xl bg-[#6e48b5] text-xs hover:bg-[#5a3b94]">
                  {loading ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
                  Attiva
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const OSS_PUSH_OPTIONS = {
  statusPath: "/api/oss/push/status",
  subscribePath: "/api/oss/push/subscribe",
  requireLocalSubscription: true,
};

export const ADMIN_PUSH_OPTIONS = {
  statusPath: "/api/admin/push/status",
  subscribePath: "/api/admin/push/subscribe",
  requireLocalSubscription: true,
};

const ADMIN_COMMERCIALE_PUSH_MODULES = [
  "commerciale-richieste-sito",
  "commerciale-richieste-cdrpt",
  "commerciale-candidature-cdrpt",
  "commerciale-dashboard",
  "commerciale-lead-strutture",
  "leads-admin",
];

export function adminReceivesWebsiteRequestPush(user) {
  if (!user) return false;
  if (user.scope === "super_admin") return true;
  const modules = Array.isArray(user.modules) ? user.modules : null;
  if (modules == null) return user.scope === "crm_operatore";
  return modules.some((moduleId) => ADMIN_COMMERCIALE_PUSH_MODULES.includes(moduleId));
}

export function AdminPushNotificationPrompt({ token }) {
  return (
    <PushNotificationPrompt
      token={token}
      storageKey="admin-push-prompt-dismissed"
      title="Attiva notifiche commerciali"
      description="Ricevi un avviso sul dispositivo quando arriva una nuova richiesta da rco-group.it, casadiriposoperte.it o casadiriposoperte.app, o quando una struttura si registra su Affidya."
      pushOptions={ADMIN_PUSH_OPTIONS}
      iconWrapClassName="w-11 h-11 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0"
      iconClassName="w-5 h-5 text-indigo-700"
      actionClassName="flex-1 h-9 text-xs bg-[#3F51B5] hover:bg-[#32408f]"
    />
  );
}

export function AdminPushMenuControls({ token }) {
  const { supported, configured, subscribed, loading, subscribe, unsubscribe } =
    usePushNotifications(token, ADMIN_PUSH_OPTIONS);
  if (!token || !supported || !configured) return null;
  return (
    <DropdownMenuItem
      disabled={loading}
      onSelect={(event) => {
        event.preventDefault();
        if (subscribed) unsubscribe();
        else subscribe();
      }}
    >
      <Bell className="mr-2 h-4 w-4" />
      {loading ? "Attendi..." : subscribed ? "Disattiva notifiche" : "Attiva notifiche"}
    </DropdownMenuItem>
  );
}

/** Prompt dedicato area OSS (terapie imminenti sul tablet). */
export function OssPushNotificationPrompt({ token }) {
  return (
    <PushNotificationPrompt
      token={token}
      storageKey="oss-push-prompt-dismissed"
      title="Attiva avvisi terapie"
      description="Ricevi una notifica sul tablet quando una terapia è in scadenza tra 15 minuti. Vale per questo dispositivo e tutti gli OSS della struttura."
      pushOptions={OSS_PUSH_OPTIONS}
    />
  );
}

export function PushNotificationsAdminModule({ users = [], loadingUsers }) {
  const [config, setConfig] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [subsSummary, setSubsSummary] = useState({ total_users: 0, total_subscriptions: 0 });
  const [loading, setLoading] = useState(false);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [sending, setSending] = useState(false);
  const [userId, setUserId] = useState("");
  const [eventType, setEventType] = useState("");
  const [titolo, setTitolo] = useState("");
  const [messaggio, setMessaggio] = useState("");
  const [lastResult, setLastResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyChannel, setHistoryChannel] = useState("all");
  const [historyEventType, setHistoryEventType] = useState("");

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem("super_admin_token");

  const loadSubscriptions = useCallback(async () => {
    if (!token) return;
    setLoadingSubs(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/push-notifications/subscriptions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSubscriptions(data.items || []);
        setSubsSummary({
          total_users: data.total_users || 0,
          total_subscriptions: data.total_subscriptions || 0,
        });
      }
    } catch (error) {
      console.error("Errore caricamento subscription push:", error);
    } finally {
      setLoadingSubs(false);
    }
  }, [API_URL, token]);

  const loadHistory = useCallback(async () => {
    if (!token) return;
    setLoadingHistory(true);
    try {
      const params = new URLSearchParams({ limit: "50", skip: "0" });
      if (historyChannel && historyChannel !== "all") {
        params.set("channel", historyChannel);
      }
      if (historyEventType) {
        params.set("event_type", historyEventType);
      }
      const res = await fetch(`${API_URL}/api/admin/push-notifications/log?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setHistory(data.items || []);
        setHistoryTotal(data.total || 0);
      }
    } catch (error) {
      console.error("Errore caricamento storico notifiche:", error);
    } finally {
      setLoadingHistory(false);
    }
  }, [API_URL, token, historyChannel, historyEventType]);

  const loadMeta = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [cfgRes, typesRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/push-notifications/config`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/api/admin/push-notifications/event-types`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      if (cfgRes.ok) setConfig(await cfgRes.json());
      if (typesRes.ok) {
        const data = await typesRes.json();
        setEventTypes(data.items || []);
        if ((data.items || []).length && !eventType) {
          setEventType(data.items[0].id);
        }
      }
      await loadSubscriptions();
    } catch (error) {
      toast.error("Errore caricamento config push");
    } finally {
      setLoading(false);
    }
  }, [API_URL, token, eventType, loadSubscriptions]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleSendTest = async () => {
    if (!userId || !eventType) {
      toast.error("Seleziona utente e tipo evento");
      return;
    }
    setSending(true);
    setLastResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/push-notifications/test`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: userId,
          event_type: eventType,
          titolo: titolo.trim() || undefined,
          messaggio: messaggio.trim() || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || "Invio fallito");
      }
      setLastResult(data);
      toast.success(`Push inviata (${data.sent || 0} dispositivi)`);
      loadSubscriptions();
      loadHistory();
    } catch (error) {
      toast.error(error.message || "Errore invio push");
    } finally {
      setSending(false);
    }
  };

  const userList = users?.users || users || [];

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("it-IT");
    } catch {
      return iso;
    }
  };

  const statusLabel = (row) => {
    if (row.channel === "in_app") return row.read ? "Letta" : "In-app";
    const map = {
      sent: "Inviata",
      failed: "Fallita",
      skipped: "Saltata",
      no_targets: "Nessun dispositivo",
      in_app: "In-app",
    };
    return map[row.status] || row.status || "—";
  };

  const statusBadgeClass = (row) => {
    if (row.channel === "in_app") {
      return row.read ? "bg-slate-100 text-slate-600" : "bg-blue-100 text-blue-800";
    }
    switch (row.status) {
      case "sent":
        return "bg-emerald-100 text-emerald-800";
      case "failed":
        return "bg-red-100 text-red-800";
      case "skipped":
        return "bg-amber-100 text-amber-800";
      default:
        return "bg-slate-100 text-slate-600";
    }
  };

  const channelLabel = (ch) => {
    if (ch === "push") return "Push";
    if (ch === "in_app") return "In-app";
    return ch || "—";
  };

  const formatRecipients = (row) => {
    if (row.channel === "in_app") return "—";
    const recipients = row.recipients || [];
    if (recipients.length === 0 && row.target_user_ids?.length) {
      return `${row.target_user_ids.length} utente/i`;
    }
    if (recipients.length === 0) return "—";
    const names = recipients
      .slice(0, 2)
      .map((r) => r.name || r.email || r.user_id)
      .filter(Boolean);
    const extra = recipients.length > 2 ? ` +${recipients.length - 2}` : "";
    return names.join(", ") + extra;
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-emerald-600" />
            Utenti con notifiche push attive
          </CardTitle>
          <CardDescription>
            {subsSummary.total_users} utenti · {subsSummary.total_subscriptions} dispositivi registrati
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-end">
            <Button size="sm" variant="outline" onClick={loadSubscriptions} disabled={loadingSubs} className="gap-2">
              <RefreshCw className={`w-4 h-4 ${loadingSubs ? "animate-spin" : ""}`} />
              Aggiorna elenco
            </Button>
          </div>
          {loadingSubs && subscriptions.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-6 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" />
              Caricamento subscription…
            </div>
          ) : subscriptions.length === 0 ? (
            <p className="text-sm text-slate-500 py-4 text-center">
              Nessun utente ha ancora attivato le notifiche push.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium text-slate-600">Utente</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Struttura</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Ruolo</th>
                    <th className="px-3 py-2 font-medium text-slate-600 text-center">Dispositivi</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Ultimo aggiornamento</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map((row) => (
                    <tr key={row.user_id} className="border-t border-slate-100 hover:bg-slate-50/80">
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-900">{row.name}</div>
                        <div className="text-xs text-slate-500">{row.email}</div>
                      </td>
                      <td className="px-3 py-2 text-slate-600">
                        {row.organization_name || row.subscription_org_ids?.[0] || "—"}
                      </td>
                      <td className="px-3 py-2 text-slate-600">{row.role}</td>
                      <td className="px-3 py-2 text-center">
                        <Badge variant="outline">{row.devices_count}</Badge>
                      </td>
                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                        {formatDate(row.last_subscription_at)}
                      </td>
                      <td className="px-3 py-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 text-xs"
                          onClick={() => setUserId(row.user_id)}
                        >
                          Usa per test
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-emerald-600" />
            Notifiche push — test
          </CardTitle>
          <CardDescription>
            Invia una notifica push di prova a un utente registrato
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={config?.configured ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>
              {config?.configured ? "VAPID configurato" : "VAPID non configurato"}
            </Badge>
            {config?.subscriptions_count != null && (
              <Badge variant="outline">{config.subscriptions_count} subscription attive</Badge>
            )}
            <Button size="sm" variant="ghost" onClick={() => { loadMeta(); loadHistory(); }} disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Utente destinatario</Label>
              <Select value={userId} onValueChange={setUserId}>
                <SelectTrigger>
                  <SelectValue placeholder={loadingUsers ? "Caricamento..." : "Seleziona utente"} />
                </SelectTrigger>
                <SelectContent>
                  {(Array.isArray(userList) ? userList : []).map((u) => (
                    <SelectItem key={u.id || u._id} value={String(u.id || u._id)}>
                      {(u.name || u.nome || u.email)} — {u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tipo evento</Label>
              <Select value={eventType} onValueChange={setEventType}>
                <SelectTrigger>
                  <SelectValue placeholder="Tipo notifica" />
                </SelectTrigger>
                <SelectContent>
                  {eventTypes.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Titolo (opzionale)</Label>
              <Input value={titolo} onChange={(e) => setTitolo(e.target.value)} placeholder="Override titolo" />
            </div>
            <div className="space-y-2">
              <Label>Messaggio (opzionale)</Label>
              <Input value={messaggio} onChange={(e) => setMessaggio(e.target.value)} placeholder="Override messaggio" />
            </div>
          </div>

          <Button onClick={handleSendTest} disabled={sending || !config?.configured} className="gap-2">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Invia notifica di test
          </Button>

          {lastResult && (
            <pre className="text-xs bg-slate-50 rounded-lg p-3 overflow-auto">
              {JSON.stringify(lastResult, null, 2)}
            </pre>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-emerald-600" />
            Storico notifiche inviate
          </CardTitle>
          <CardDescription>
            Push web e notifiche in-app registrate nel sistema ({historyTotal} totali)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Canale</Label>
              <Select value={historyChannel} onValueChange={setHistoryChannel}>
                <SelectTrigger className="w-[140px] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="push">Push</SelectItem>
                  <SelectItem value="in_app">In-app</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Tipo evento</Label>
              <Select
                value={historyEventType || "__all__"}
                onValueChange={(v) => setHistoryEventType(v === "__all__" ? "" : v)}
              >
                <SelectTrigger className="w-[220px] h-9">
                  <SelectValue placeholder="Tutti i tipi" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Tutti i tipi</SelectItem>
                  {eventTypes.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={loadHistory}
              disabled={loadingHistory}
              className="gap-2 h-9"
            >
              <RefreshCw className={`w-4 h-4 ${loadingHistory ? "animate-spin" : ""}`} />
              Aggiorna
            </Button>
          </div>

          {loadingHistory && history.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-6 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" />
              Caricamento storico…
            </div>
          ) : history.length === 0 ? (
            <p className="text-sm text-slate-500 py-4 text-center">
              Nessuna notifica registrata. Le push inviate da ora compariranno qui; le in-app provengono dallo storico esistente.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium text-slate-600">Data</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Canale</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Tipo</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Titolo</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Destinatari</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Esito</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Struttura</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((row) => (
                    <tr key={`${row.channel}-${row.id}`} className="border-t border-slate-100 hover:bg-slate-50/80">
                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                        {formatDate(row.created_at)}
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant="outline">{channelLabel(row.channel)}</Badge>
                        {row.is_test && (
                          <Badge className="ml-1 bg-purple-100 text-purple-800">Test</Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-600">
                        {row.event_label || row.event_type || row.tipo || "—"}
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-900 max-w-[200px] truncate" title={row.titolo}>
                          {row.titolo || "—"}
                        </div>
                        {row.messaggio && (
                          <div className="text-xs text-slate-500 max-w-[200px] truncate" title={row.messaggio}>
                            {row.messaggio}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-600 text-xs">
                        {formatRecipients(row)}
                        {row.channel === "push" && row.sent_count != null && (
                          <div className="text-slate-400">
                            {row.sent_count} ok
                            {row.failed_count > 0 ? ` · ${row.failed_count} err` : ""}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <Badge className={statusBadgeClass(row)}>{statusLabel(row)}</Badge>
                        {row.skip_reason && (
                          <div className="text-xs text-slate-400 mt-0.5">{row.skip_reason}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-600 text-xs">
                        {row.organization_name || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
